import React, { useMemo } from 'react';
import { AlertTriangle, ExternalLink, Info, Loader2, MessageSquareWarning, RefreshCw } from 'lucide-react';
import type { ServiceItem } from '../../types';
import type { AbweichungsStand } from '../../services/projektSteckbrief/abweichungen';
import type { AusgabeUnterlage } from '../../services/unterlagenAusgabenService';
import { useDokumente } from '../dokumente/useDokumente';
import type { PflichtEintrag } from './types';
import {
    ausgabenAusStaenden,
    lesefehlerSaetze,
    uebergabeZeilen,
    type UebergabePflichtStand,
    type UebergabeStandArt,
    type UebergabeZeile,
} from './uebergabeLogic';

interface Props {
    projectId: string;
    /** `pflichtenStand` aus den GESPEICHERTEN Merkmalen; `null`, wenn die Phase nicht gelesen werden konnte. */
    pflichten: readonly PflichtEintrag[] | null;
    pflichtenFehler: string | null;
    /** Der Aenderungsdienst (`useAbweichungen`). */
    staende: readonly AbweichungsStand[];
    staendeBereit: boolean;
    staendeLesefehler: string | null;
    /** Die Leistungsliste des Projekts — nur Anzeige ueber `calculateStats`. */
    lv: readonly ServiceItem[];
    onNeuLaden: () => void;
    onZumModul: (zeile: UebergabeZeile) => void;
    /** Oeffnet die Rueckfrage (drei Knoepfe) fuer die offenen Staende dieser Unterlage. */
    onRueckfrage: (unterlage: AusgabeUnterlage) => void;
}

const STAND_LABEL: Record<UebergabeStandArt, string> = {
    fehlt: 'fehlt',
    entwurf: 'Entwurf',
    freigegeben: 'freigegeben',
    versendet: 'versendet',
    veraltet: 'veraltet',
    nicht_erforderlich: 'nicht erforderlich',
    unbekannt: 'nicht lesbar',
};

const STAND_FARBE: Record<UebergabeStandArt, string> = {
    fehlt: 'bg-slate-100 text-slate-600 border-slate-200',
    entwurf: 'bg-blue-50 text-blue-700 border-blue-200',
    freigegeben: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    versendet: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    veraltet: 'bg-amber-50 text-amber-800 border-amber-300',
    nicht_erforderlich: 'bg-slate-50 text-slate-500 border-slate-200',
    unbekannt: 'bg-rose-50 text-rose-700 border-rose-200',
};

const PFLICHT_FARBE: Record<UebergabePflichtStand, string> = {
    pflicht: 'bg-blue-50 text-blue-700 border-blue-200',
    keine: 'bg-slate-100 text-slate-500 border-slate-200',
    ungeklaert: 'bg-amber-50 text-amber-800 border-amber-200',
    nach_auftrag: 'bg-slate-50 text-slate-600 border-slate-200',
};

const CHIP = 'inline-flex items-center text-[10px] font-black px-2 py-0.5 rounded border uppercase tracking-wide whitespace-nowrap';
const KNOPF = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border disabled:opacity-40';

/**
 * Reiter „Übergabe" — nur lesend (Plan Stufe 6.2).
 *
 * Je Folgeunterlage: Pflicht (dreiwertig, mit Begruendung aus
 * `pflichtenStand`), Stand (fehlt / Entwurf / freigegeben / versendet /
 * veraltet / nicht erforderlich), die letzte Entscheidung des
 * Aenderungsdiensts, der LV-Bezug und der Sprung ins Modul. Die Regeln dazu
 * stehen geprueft in `uebergabeLogic.ts`; diese Komponente zeigt an und ruft
 * auf.
 *
 * Gebucht wird hier nichts. Auch „Rückfrage öffnen" schreibt nicht selbst —
 * es oeffnet den Dialog mit den drei Knoepfen (Stufe 6.1), und der schreibt
 * nur auf Klick ueber den Aenderungsdienst.
 *
 * ═══ ZWEI QUELLEN, ZWEI LESEFEHLER ════════════════════════════════════════
 * Die Ausgaben samt Abweichungen kommen vom Aenderungsdienst (Props), die
 * Entwuerfe aus der Projektablage (`useDokumente`, hier geladen, weil nur
 * dieser Reiter sie braucht). Beide koennen scheitern; jeder Fehler steht
 * als Satz ueber der Liste, und ein Fehler des Aenderungsdiensts macht jeden
 * Stand „nicht lesbar" — nie still „fehlt" (Hausregel: Ausfall darf nicht
 * wie Entwarnung aussehen).
 *
 * Vor `bereit` werden keine Zeilen gezeichnet: eine leere Staende-Liste
 * heisst dort „noch nicht gelesen", und sechsmal „fehlt" waere eine falsche
 * Aussage fuer eine halbe Sekunde.
 */
export const UebergabeReiter: React.FC<Props> = ({
    projectId, pflichten, pflichtenFehler, staende, staendeBereit, staendeLesefehler, lv,
    onNeuLaden, onZumModul, onRueckfrage,
}) => {
    const ablage = useDokumente(projectId);

    const lesefehler = useMemo(
        () => ({ aenderungsdienst: staendeLesefehler, ablage: ablage.fehler }),
        [staendeLesefehler, ablage.fehler],
    );

    const zeilen = useMemo(
        () => uebergabeZeilen(pflichten, ausgabenAusStaenden(staende), staende, ablage.dokumente, lv, lesefehler),
        [pflichten, staende, ablage.dokumente, lv, lesefehler],
    );

    const bereit = staendeBereit && !ablage.laden;
    const saetze = bereit ? lesefehlerSaetze(lesefehler, pflichtenFehler) : [];
    const veraltet = bereit ? zeilen.filter(z => z.handlungsbedarf).length : 0;

    const neuLaden = () => { onNeuLaden(); void ablage.neuLaden(); };

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-start gap-3 flex-wrap">
                <Info className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-500 flex-1 min-w-[16rem] leading-snug">
                    Nur Ansicht. Die Pflicht kommt aus den gespeicherten Merkmalen der Planungsphase, der Stand aus der
                    Projektablage und dem Änderungsdienst, der LV-Bezug aus der Leistungsliste. Erstellt, freigegeben und
                    gebucht wird in den Modulen.
                    {veraltet > 0 && (
                        <>
                            {' '}<strong className="text-amber-800">
                                {veraltet === 1 ? 'Eine Unterlage ist veraltet' : `${veraltet} Unterlagen sind veraltet`}
                            </strong>: die Planungsdaten weichen von der geltenden Fassung ab, ohne Entscheidung dazu.
                        </>
                    )}
                </p>
                <button
                    type="button"
                    onClick={neuLaden}
                    disabled={!bereit}
                    className={`${KNOPF} text-slate-700 bg-white border-slate-300 hover:bg-slate-50`}
                >
                    {bereit ? <RefreshCw className="w-3.5 h-3.5" /> : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Neu laden
                </button>
            </div>

            {saetze.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <ul className="space-y-1 text-xs">
                        {saetze.map(s => <li key={s}>{s}</li>)}
                    </ul>
                </div>
            )}

            {!bereit ? (
                <p className="text-sm text-slate-400 py-10 text-center">Übergabestand wird gelesen …</p>
            ) : (
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
                    {zeilen.map(z => (
                        <div
                            key={z.unterlage}
                            className={`px-4 py-3 grid grid-cols-1 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.6fr)_auto] gap-3 md:items-start ${
                                z.handlungsbedarf ? 'bg-amber-50/40' : ''
                            }`}
                        >
                            {/* ─── Unterlage und Pflicht ───────────────── */}
                            <div className="min-w-0">
                                <div className="text-sm font-bold text-slate-800">{z.titel}</div>
                                <div className="mt-1 flex items-center gap-2 flex-wrap">
                                    <span className={`${CHIP} ${PFLICHT_FARBE[z.pflicht.stand]}`} title={z.pflicht.rechtsgrundlage || undefined}>
                                        {z.pflicht.text}
                                    </span>
                                    {z.pflicht.rechtsgrundlage && (
                                        <span className="text-[11px] text-slate-400">{z.pflicht.rechtsgrundlage}</span>
                                    )}
                                </div>
                                {z.pflicht.begruendung && (
                                    <p className="mt-1 text-[11px] text-slate-500 leading-snug">{z.pflicht.begruendung}</p>
                                )}
                            </div>

                            {/* ─── Stand und Entscheidung ──────────────── */}
                            <div className="min-w-0">
                                <div className="flex items-start gap-2 flex-wrap">
                                    <span className={`${CHIP} ${STAND_FARBE[z.stand.art]}`}>{STAND_LABEL[z.stand.art]}</span>
                                    <span className="text-xs text-slate-700 min-w-0 break-words">{z.stand.text}</span>
                                </div>
                                {z.entscheidung && (
                                    <p className="mt-1 text-[11px] text-slate-500 leading-snug">
                                        <span className="font-bold">Letzte Entscheidung:</span> {z.entscheidung}
                                    </p>
                                )}
                                <p className="mt-1 text-[11px] text-slate-400">LV: {z.lv.text}</p>
                            </div>

                            {/* ─── Wege ────────────────────────────────── */}
                            <div className="flex items-center gap-2 flex-wrap md:justify-end">
                                {z.handlungsbedarf && (
                                    <button
                                        type="button"
                                        onClick={() => onRueckfrage(z.ausgabeUnterlage)}
                                        className={`${KNOPF} text-amber-900 bg-white border-amber-300 hover:bg-amber-100`}
                                        title="Neue Fassung anlegen, nicht erheblich oder später — die Rückfrage mit den drei Knöpfen"
                                    >
                                        <MessageSquareWarning className="w-3.5 h-3.5" /> Entscheiden
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => onZumModul(z)}
                                    className={`${KNOPF} text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100`}
                                >
                                    <ExternalLink className="w-3.5 h-3.5" /> Zum Modul
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
